import { zodResolver } from "@hookform/resolvers/zod"
import { useAtomValue } from "jotai"
import { ArrowLeft, Search, UserRound } from "lucide-react"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useSearchParams } from "react-router"
import { toast } from "sonner"
import { z } from "zod"
import { ticketApi, ticketSchemas } from "@/api/client/ticket"
import { getErrorMessage } from "@/api/instance"
import {
  type ContactCategory,
  ContactPersonSelector,
  ContactTypeSelector,
  ContractorSearchModal,
  DEFAULT_TICKET_CONTACT_CONTENT,
  OcAssigneeSelector,
  type PersonType,
  type SelectedContractor,
  type TicketCategory,
  TicketCategorySelector,
  TicketStatusSelector,
  type TicketSubCategory,
} from "@/components/ticket"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { DateTimePickerField } from "@/components/ui/date-time-picker"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { RequiredBadge } from "@/components/ui/required-badge"
import { sessionUserAtom } from "@/store/session"

// フォームのスキーマを定義
const formSchema = ticketSchemas.ticketCreateBody
  .omit({
    ownerId: true,
    propertyId: true,
    roomId: true,
  })
  .extend({
    title: z.string().trim().min(1, "案件タイトルは必須です"),
  })
  .superRefine((data, ctx) => {
    if (
      data.contactFromCategory &&
      !data.contactFromOwnerId &&
      !data.contactFromTenantId &&
      !data.contactFromAdminId &&
      !data.contactFromText?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contactFromText"],
        message: "その他（FROM）は必須です",
      })
    }
    if (
      data.contactToCategory &&
      !data.contactToOwnerId &&
      !data.contactToTenantId &&
      !data.contactToAdminId &&
      !data.contactToText?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contactToText"],
        message: "その他（TO）は必須です",
      })
    }
  })

type FormValues = z.infer<typeof formSchema>

const getNumberSearchParam = (searchParams: URLSearchParams, key: string) => {
  const value = searchParams.get(key)
  if (!value) return undefined

  const parsedValue = Number.parseInt(value, 10)
  return Number.isNaN(parsedValue) ? undefined : parsedValue
}

const getInitialSelectedProperty = (searchParams: URLSearchParams): SelectedContractor | null => {
  const tenantId = getNumberSearchParam(searchParams, "tenantId")
  const propertyId = getNumberSearchParam(searchParams, "propertyId")
  const roomId = getNumberSearchParam(searchParams, "roomId")
  const ownerId = searchParams.get("ownerId") || undefined

  if (!tenantId || !propertyId || !roomId || !ownerId) {
    return null
  }

  return {
    tenantId,
    ownerId,
    propertyId,
    roomId,
    tenant: searchParams.get("tenantName") || "",
    property: searchParams.get("propertyName") || "",
    plan: searchParams.get("planName") || "-",
    room: searchParams.get("roomName") || "",
    phone: searchParams.get("phone") || "",
    owner: searchParams.get("ownerName") || "",
    startDate: searchParams.get("startDate") || "",
    propertyNote: searchParams.get("propertyNote") || undefined,
    tenantAdminMemo: searchParams.get("tenantAdminMemo") || undefined,
    ownerAdminMemo: searchParams.get("ownerAdminMemo") || undefined,
  }
}

export function TicketAddPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sessionUser = useAtomValue(sessionUserAtom)

  // 契約者検索モーダルの開閉状態
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

  // 選択された契約者情報
  const [selectedProperty, setSelectedProperty] = useState<SelectedContractor | null>(() =>
    getInitialSelectedProperty(searchParams)
  )

  // 契約者未選択エラー
  const [contractorError, setContractorError] = useState<string | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      contactType: "phone",
      contactToCategory: "oc",
      status: "oc_in_progress",
      occurredAt: new Date().toISOString(),
      content: DEFAULT_TICKET_CONTACT_CONTENT,
      ocAssigneeId: sessionUser?.id ?? "",
      alertComment: "",
      ocComment: "",
      note: "",
    },
  })

  const currentAdminName = sessionUser?.name || sessionUser?.email || ""

  useEffect(() => {
    if (!sessionUser?.id) return
    form.setValue("ocAssigneeId", sessionUser.id)
    if (form.getValues("contactToCategory") === "oc" && !form.getValues("contactToAdminId")) {
      form.setValue("contactToAdminId", sessionUser.id)
    }
  }, [form, sessionUser?.id])

  const setFromCurrentAdmin = () => {
    if (!sessionUser?.id) return
    form.setValue("contactFromCategory", "oc" as FormValues["contactFromCategory"])
    form.setValue("contactFromOwnerId", undefined)
    form.setValue("contactFromTenantId", undefined)
    form.setValue("contactFromAdminId", sessionUser.id)
    form.setValue("contactFromText", "")
  }

  const setToCurrentAdmin = () => {
    if (!sessionUser?.id) return
    form.setValue("contactToCategory", "oc" as FormValues["contactToCategory"])
    form.setValue("contactToOwnerId", undefined)
    form.setValue("contactToTenantId", undefined)
    form.setValue("contactToAdminId", sessionUser.id)
    form.setValue("contactToText", "")
  }

  // 連絡者ID変更ハンドラ (FROM)
  const handleFromPersonIdChange = (value: string, type: PersonType) => {
    if (type === "owner") {
      form.setValue("contactFromOwnerId", value || undefined)
      form.setValue("contactFromTenantId", undefined)
      form.setValue("contactFromAdminId", undefined)
    } else if (type === "oc") {
      form.setValue("contactFromAdminId", value || undefined)
      form.setValue("contactFromOwnerId", undefined)
      form.setValue("contactFromTenantId", undefined)
    } else {
      form.setValue("contactFromTenantId", value ? Number.parseInt(value, 10) : undefined)
      form.setValue("contactFromOwnerId", undefined)
      form.setValue("contactFromAdminId", undefined)
    }
  }

  // 連絡者ID変更ハンドラ (TO)
  const handleToPersonIdChange = (value: string, type: PersonType) => {
    if (type === "owner") {
      form.setValue("contactToOwnerId", value || undefined)
      form.setValue("contactToTenantId", undefined)
      form.setValue("contactToAdminId", undefined)
    } else if (type === "oc") {
      form.setValue("contactToAdminId", value || undefined)
      form.setValue("contactToOwnerId", undefined)
      form.setValue("contactToTenantId", undefined)
    } else {
      form.setValue("contactToTenantId", value ? Number.parseInt(value, 10) : undefined)
      form.setValue("contactToOwnerId", undefined)
      form.setValue("contactToAdminId", undefined)
    }
  }

  const createMutation = ticketApi.useTicketCreate({
    mutation: {
      onSuccess: (createdTicket) => {
        toast.success("案件を作成しました")
        navigate(`/tickets/${createdTicket.id}`, { state: { shouldRefetch: true } })
      },
      onError: (error) => {
        toast.error("案件の作成に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const handleSubmit = async (values: FormValues) => {
    try {
      const payload = {
        ...values,
        ownerId: selectedProperty?.ownerId,
        propertyId: selectedProperty?.propertyId,
        roomId: selectedProperty?.roomId,
      }

      // Zodスキーマでバリデーション
      const validatedData = ticketSchemas.ticketCreateBody.parse(payload)

      // API呼び出し
      await createMutation.mutateAsync({ data: validatedData })
    } catch (error) {
      console.error(error)
      // エラーハンドリングはmutationのonErrorで処理される
    }
  }

  // フォーム送信前に契約者チェックを行う
  const handleFormSubmit = (e: React.FormEvent) => {
    // 契約者未選択チェック（フォームバリデーション前に実行）
    if (!selectedProperty?.ownerId) {
      setContractorError("契約者情報を選択してください")
      e.preventDefault()
      return
    }
    setContractorError(null)
    // react-hook-formのhandleSubmitを呼び出し
    form.handleSubmit(handleSubmit)(e)
  }

  const handleSearchContractor = () => {
    setIsSearchModalOpen(true)
  }

  const handleSelectContractor = (contractor: SelectedContractor) => {
    setSelectedProperty({
      tenantId: contractor.tenantId,
      ownerId: contractor.ownerId,
      propertyId: contractor.propertyId,
      roomId: contractor.roomId,
      tenant: contractor.tenant,
      property: contractor.property,
      plan: contractor.plan,
      room: contractor.room,
      phone: contractor.phone,
      owner: contractor.owner,
      startDate: contractor.startDate,
      propertyNote: contractor.propertyNote,
      tenantAdminMemo: contractor.tenantAdminMemo,
      ownerAdminMemo: contractor.ownerAdminMemo,
    })
    // エラーをクリア
    setContractorError(null)
  }

  return (
    <ReactErrorBoundary>
      <div>
        {/* ヘッダーナビゲーション */}
        <div className="flex items-center gap-2 p-6">
          <button
            type="button"
            onClick={() => navigate("/tickets")}
            className="flex items-center justify-center w-7 h-7 bg-white rounded-[5px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]"
          >
            <ArrowLeft size={14} className="text-[#354267]" />
          </button>
          <span className="text-sm font-semibold text-[#3f4652]">案件一覧に戻る</span>
        </div>

        <div className="max-w-[895px] mx-auto px-6 pb-8">
          <Form {...form}>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              {/* タイトルバー */}
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-lg font-semibold text-[#3f4652]">新規案件作成</h1>
              </div>

              {/* 契約者情報カード */}
              <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
                <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-semibold text-[#3f4652]">
                        契約者情報（対象物件）
                      </h2>
                      <RequiredBadge />
                      {contractorError && (
                        <span className="text-xs text-red-500">{contractorError}</span>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSearchContractor}
                      className="h-8"
                    >
                      <Search size={14} />
                      契約者検索
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-5">
                  {selectedProperty ? (
                    <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                      <div>
                        <p className="text-xs font-medium text-[#9eafc7] mb-2">賃借人</p>
                        <p className="text-sm text-[#3f4652]">{selectedProperty.tenant || "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[#9eafc7] mb-2">物件名</p>
                        <p className="text-sm text-[#3f4652]">{selectedProperty.property || "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[#9eafc7] mb-2">加入プラン</p>
                        <p className="text-sm text-[#3f4652]">{selectedProperty.plan || "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[#9eafc7] mb-2">部屋番号</p>
                        <p className="text-sm text-[#3f4652]">{selectedProperty.room || "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[#9eafc7] mb-2">電話番号</p>
                        <p className="text-sm text-[#3f4652]">{selectedProperty.phone || "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[#9eafc7] mb-2">オーナー名</p>
                        <p className="text-sm text-[#3f4652]">{selectedProperty.owner || "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[#9eafc7] mb-2">賃貸借契約開始日</p>
                        <p className="text-sm text-[#3f4652]">
                          {selectedProperty.startDate || "-"}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-8">
                      契約者を検索してください
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* 連絡先情報カード */}
              <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
                <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                  <h2 className="text-sm font-semibold text-[#3f4652]">
                    連絡先情報（問い合わせ先）
                  </h2>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  <div className="grid grid-cols-3 gap-x-8">
                    <FormField
                      control={form.control}
                      name="contactType"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            連絡区分
                            <RequiredBadge />
                          </FormLabel>
                          <FormControl>
                            <ContactTypeSelector
                              value={field.value}
                              onChange={field.onChange}
                              error={fieldState.error?.message}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xs font-semibold text-[#3f4652]">連絡者区分（FROM）</p>
                      <RequiredBadge />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-[#354267]"
                        onClick={setFromCurrentAdmin}
                        disabled={!sessionUser?.id}
                        aria-label="ログインユーザーを連絡者（FROM）に入力"
                        title="ログインユーザーを入力"
                      >
                        <UserRound size={14} />
                      </Button>
                    </div>
                    <ContactPersonSelector
                      direction="from"
                      categoryValue={
                        (form.watch("contactFromCategory") || "") as ContactCategory | ""
                      }
                      personIdValue={
                        form.watch("contactFromOwnerId") ||
                        form.watch("contactFromTenantId")?.toString() ||
                        form.watch("contactFromAdminId") ||
                        ""
                      }
                      textValue={form.watch("contactFromText") || ""}
                      onCategoryChange={(value) => {
                        form.setValue(
                          "contactFromCategory",
                          value as FormValues["contactFromCategory"]
                        )
                        // カテゴリ変更時にIDをリセット
                        form.setValue("contactFromOwnerId", undefined)
                        form.setValue("contactFromTenantId", undefined)
                        form.setValue("contactFromAdminId", undefined)

                        // 契約者情報が選択されている場合、自動的に該当する連絡者を選択
                        if (selectedProperty) {
                          if (value === "owner" && selectedProperty.ownerId) {
                            form.setValue("contactFromOwnerId", selectedProperty.ownerId)
                          } else if (value === "tenant" && selectedProperty.tenantId) {
                            form.setValue("contactFromTenantId", selectedProperty.tenantId)
                          }
                        }
                        if (value === "oc" && sessionUser?.id) {
                          form.setValue("contactFromAdminId", sessionUser.id)
                        }
                      }}
                      onPersonIdChange={handleFromPersonIdChange}
                      onTextChange={(value) => form.setValue("contactFromText", value)}
                      categoryError={form.formState.errors.contactFromCategory?.message}
                      textError={form.formState.errors.contactFromText?.message}
                      selectedOwnerId={selectedProperty?.ownerId}
                      selectedOwnerName={selectedProperty?.owner}
                      selectedTenantId={selectedProperty?.tenantId}
                      selectedTenantName={selectedProperty?.tenant}
                      currentAdminId={sessionUser?.id}
                      currentAdminName={currentAdminName}
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xs font-semibold text-[#3f4652]">連絡者（TO）</p>
                      <RequiredBadge />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-[#354267]"
                        onClick={setToCurrentAdmin}
                        disabled={!sessionUser?.id}
                        aria-label="ログインユーザーを連絡者（TO）に入力"
                        title="ログインユーザーを入力"
                      >
                        <UserRound size={14} />
                      </Button>
                    </div>
                    <ContactPersonSelector
                      direction="to"
                      categoryValue={
                        (form.watch("contactToCategory") || "") as ContactCategory | ""
                      }
                      personIdValue={
                        form.watch("contactToOwnerId") ||
                        form.watch("contactToTenantId")?.toString() ||
                        form.watch("contactToAdminId") ||
                        ""
                      }
                      textValue={form.watch("contactToText") || ""}
                      onCategoryChange={(value) => {
                        form.setValue("contactToCategory", value as FormValues["contactToCategory"])
                        // カテゴリ変更時にIDをリセット
                        form.setValue("contactToOwnerId", undefined)
                        form.setValue("contactToTenantId", undefined)
                        form.setValue("contactToAdminId", undefined)

                        // 契約者情報が選択されている場合、自動的に該当する連絡者を選択
                        if (selectedProperty) {
                          if (value === "owner" && selectedProperty.ownerId) {
                            form.setValue("contactToOwnerId", selectedProperty.ownerId)
                          } else if (value === "tenant" && selectedProperty.tenantId) {
                            form.setValue("contactToTenantId", selectedProperty.tenantId)
                          }
                        }
                        if (value === "oc" && sessionUser?.id) {
                          form.setValue("contactToAdminId", sessionUser.id)
                        }
                      }}
                      onPersonIdChange={handleToPersonIdChange}
                      onTextChange={(value) => form.setValue("contactToText", value)}
                      categoryError={form.formState.errors.contactToCategory?.message}
                      textError={form.formState.errors.contactToText?.message}
                      selectedOwnerId={selectedProperty?.ownerId}
                      selectedOwnerName={selectedProperty?.owner}
                      selectedTenantId={selectedProperty?.tenantId}
                      selectedTenantName={selectedProperty?.tenant}
                      currentAdminId={sessionUser?.id}
                      currentAdminName={currentAdminName}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* 案件情報カード */}
              <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
                <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                  <h2 className="text-sm font-semibold text-[#3f4652]">案件情報</h2>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  <FormField
                    control={form.control}
                    name="occurredAt"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          発生日時
                          <RequiredBadge />
                        </FormLabel>
                        <DateTimePickerField
                          value={field.value}
                          onChange={field.onChange}
                        />
                        {fieldState.error && (
                          <p className="text-xs text-red-500">{fieldState.error.message}</p>
                        )}
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          タイトル
                          <RequiredBadge />
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <p className="text-xs font-semibold text-[#3f4652]">
                    カテゴリ
                    <RequiredBadge />
                  </p>
                  <TicketCategorySelector
                    categoryValue={(form.watch("category") || "") as TicketCategory | ""}
                    subCategoryValue={(form.watch("subCategory") || "") as TicketSubCategory | ""}
                    onCategoryChange={(value) => {
                      form.setValue("category", value as FormValues["category"])
                      form.resetField("subCategory")
                    }}
                    onSubCategoryChange={(value) =>
                      form.setValue("subCategory", value as FormValues["subCategory"])
                    }
                    categoryError={form.formState.errors.category?.message}
                    subCategoryError={form.formState.errors.subCategory?.message}
                  />

                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          連絡内容
                          <RequiredBadge />
                        </FormLabel>
                        <FormControl>
                          <textarea
                            {...field}
                            rows={15}
                            className="w-full min-h-[80px] px-3 py-2 text-sm bg-white border border-slate-300 rounded-[5px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {(() => {
                    const warningItems: string[] = []
                    if (selectedProperty?.propertyNote)
                      warningItems.push("物件注意事項の注意事項がございます。")
                    if (selectedProperty?.tenantAdminMemo)
                      warningItems.push("賃借人注意事項の注意事項がございます。")
                    if (selectedProperty?.ownerAdminMemo)
                      warningItems.push("オーナー注意事項の注意事項がございます。")
                    return warningItems.length > 0 ? (
                      <div className="grid grid-cols-2 gap-x-8">
                        <div>
                          <p className="text-xs font-semibold text-[#3f4652] mb-2">注意事項</p>
                          <div className="p-3 bg-[#ffeeee] border border-[#ff3232] rounded-md">
                            <div className="space-y-1 text-xs text-[#3f4652]">
                              {warningItems.map((item) => (
                                <p key={item}>・{item}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null
                  })()}

                  <FormField
                    control={form.control}
                    name="note"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">備考</FormLabel>
                        <FormControl>
                          <textarea
                            {...field}
                            className="w-full min-h-[60px] px-3 py-2 text-sm bg-white border border-slate-300 rounded-[5px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-3 gap-x-8">
                    <FormField
                      control={form.control}
                      name="ocAssigneeId"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            OC担当者
                            <RequiredBadge />
                          </FormLabel>
                          <FormControl>
                            <OcAssigneeSelector
                              value={field.value || ""}
                              onChange={field.onChange}
                              error={fieldState.error?.message}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-x-8">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            ステータス
                            <RequiredBadge />
                          </FormLabel>
                          <FormControl>
                            <TicketStatusSelector
                              value={field.value}
                              onChange={field.onChange}
                              error={fieldState.error?.message}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* アラートカード */}
              <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
                <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                  <h2 className="text-sm font-semibold text-[#3f4652]">アラート</h2>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-[#3f4652]">アラート日時</p>
                    <FormField
                      control={form.control}
                      name="alertAt"
                      render={({ field }) => (
                        <DateTimePickerField
                          value={field.value}
                          onChange={field.onChange}
                          defaultTime="00:00"
                          disabled={(date) => {
                            const today = new Date()
                            today.setHours(0, 0, 0, 0)
                            return date < today
                          }}
                        />
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="alertComment"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          アラートコメント
                        </FormLabel>
                        <FormControl>
                          <textarea
                            {...field}
                            className="w-full min-h-[60px] px-3 py-2 text-sm bg-white border border-slate-300 rounded-[5px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* 注意事項カード */}
              <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
                <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                  <h2 className="text-sm font-semibold text-[#3f4652]">注意事項</h2>
                </CardHeader>
                <CardContent className="p-5">
                  <FormField
                    control={form.control}
                    name="ocComment"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          OCコメント
                        </FormLabel>
                        <FormControl>
                          <textarea
                            {...field}
                            className="w-full min-h-[60px] px-3 py-2 text-sm bg-white border border-slate-300 rounded-[5px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* 送信ボタン */}
              <div className="flex justify-center gap-4 pt-6">
                <Button
                  type="submit"
                  variant="default"
                  className="min-w-[100px]"
                  disabled={createMutation.isPending}
                  loading={createMutation.isPending}
                >
                  追加する
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/tickets")}
                  disabled={createMutation.isPending}
                >
                  キャンセル
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>

      {/* 契約者検索モーダル */}
      <ContractorSearchModal
        open={isSearchModalOpen}
        onOpenChange={setIsSearchModalOpen}
        onSelect={handleSelectContractor}
      />
    </ReactErrorBoundary>
  )
}
